import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider.js';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import UserInfo from './UserInfo';
import UserBadges from './UserBadges';
import CarManager from './CarManager';
import DetailedRatings from '../DetailedRatings';
import './MyProfile.css';

const MyProfilePage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user, refreshProfile } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [userId, setUserId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [cars, setCars] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState('ratings');

  // Loading states
  const [profileLoading, setProfileLoading] = useState(true);
  const [carsLoading, setCarsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // Profile management functions
  const updateProfile = async (profileData) => {
    try {
      setActionLoading(true);
      setValidationErrors({});
      
      // Check username uniqueness if changing username
      if (profileData.username && profileData.username !== profile.username) {
        const { data: existingUsers, error: checkError } = await supabase
          .from('profiles')
          .select('id')
          .ilike('username', profileData.username)
          .neq('id', profile.id);
          
        if (checkError) throw checkError;
        
        if (existingUsers && existingUsers.length > 0) {
          setValidationErrors({ username: t('errors.username_taken') });
          return;
        }
      }
      
      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({ 
          ...profileData,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id);

      if (error) throw error;
      
      // Update local state
      setProfile(prev => ({ ...prev, ...profileData }));
      
      // Refresh AuthContext so changes are reflected everywhere
      try {
        await refreshProfile();
        console.log('✅ Profile changes synced to AuthContext');
        
        // If country was changed, wait for React to re-render before dispatching event
        if (profileData.country_code) {
          // Give React time to update context (300ms should be enough)
          await new Promise(resolve => setTimeout(resolve, 300));
          
          console.log('🌍 Dispatching countryChanged event with:', profileData.country_code);
          window.dispatchEvent(new CustomEvent('countryChanged', {
            detail: {
              newCountry: profileData.country_code,
              message: `Country changed. Feed will show posts from ${profileData.country_code}.`
            }
          }));
        }
      } catch (error) {
        console.warn('⚠️ Could not refresh AuthContext:', error);
      }
      
      addToast({
        type: 'success',
        title: t('profile.profile_updated'),
        description: t('profile.profile_update_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      
      addToast({
        type: 'error',
        title: t('errors.update_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const updateAvatar = async (avatarSource, type = 'file') => {
    try {
      setActionLoading(true);
      
      let avatarUrl = null;
      
      // Handle different avatar source types
      if (type === 'file' && avatarSource) {
        // File upload
        const file = avatarSource;
        
        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(t('errors.file_size_limit'));
        }

        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `avatars/${userId}/${fileName}`;

        // Upload image
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl }, error: urlError } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        if (urlError) throw urlError;
        
        avatarUrl = publicUrl;
      } else if (type === 'url') {
        // Use provided URL (for default avatars)
        avatarUrl = avatarSource;
      }
      // Removed 'remove' type - users should always have an avatar

      // Update avatar in profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Update local state
      setProfile(prev => ({
        ...prev,
        avatar_url: avatarUrl
      }));
      
      addToast({
        type: 'success',
        title: t('profile.avatar_updated'),
        description: t('profile.avatar_update_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error updating avatar:', error);
      
      addToast({
        type: 'error',
        title: t('errors.update_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };
  
  // Car management functions
  const addCar = async (carData) => {
    try {
      setActionLoading(true);
      
      const { error } = await supabase
        .from('cars')
        .insert([{
          user_id: userId,
          brand: carData.brand,
          model: carData.model || null,
          year: carData.year || null,
          number: carData.number
        }]);
  
      if (error) throw error;
      
      // Refresh car list
      const { data: updatedCars, error: fetchError } = await supabase
        .from('cars')
        .select('*')
        .eq('user_id', userId);
        
      if (fetchError) throw fetchError;
      
      setCars(updatedCars || []);
      
      addToast({
        type: 'success',
        title: t('profile.car_added'),
        description: t('profile.car_add_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error adding car:', error);
      
      addToast({
        type: 'error',
        title: t('errors.car_add_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const deleteCar = async (carId) => {
    if (!window.confirm(t('profile.confirm_delete_car'))) {
      return false;
    }
    
    try {
      setActionLoading(true);
      
      const { error } = await supabase
        .from('cars')
        .delete()
        .eq('id', carId);
        
      if (error) throw error;
      
      // Update local state
      setCars(prev => prev.filter(car => car.id !== carId));
      
      addToast({
        type: 'success',
        title: t('profile.car_deleted'),
        description: t('profile.car_delete_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting car:', error);
      
      addToast({
        type: 'error',
        title: t('errors.car_delete_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  // Fetch user profile and cars
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setProfileLoading(true);
        
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error('Error fetching user:', userError.message);
          throw new Error(t('profile.error_fetch_user'));
        }
        
        if (!user) {
          navigate('/login');
          return;
        }
        
        setUserId(user.id);
        
        // Fetch profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (profileError) {
          console.error('Error fetching profile:', profileError.message);
          throw new Error(t('profile.error_fetch_profile'));
        }
        
        setProfile(profileData);
        
      } catch (error) {
        console.error('Error in fetchProfileData:', error);
        setError(error.message);
        addToast({
          type: 'error',
          title: t('errors.profile_load_error'),
          description: error.message
        });
      } finally {
        setProfileLoading(false);
      }
    };
    
    const fetchCars = async () => {
      if (!userId) return;

      try {
        setCarsLoading(true);

        const { data: carsData, error: carsError } = await supabase
          .from('cars')
          .select('*')
          .eq('user_id', userId);

        if (carsError) {
          console.error('Error fetching cars:', carsError.message);
          throw new Error(t('profile.error_fetch_cars'));
        }

        setCars(carsData || []);

      } catch (error) {
        console.error('Error in fetchCars:', error);
        addToast({
          type: 'error',
          title: t('errors.cars_load_error'),
          description: error.message
        });
      } finally {
        setCarsLoading(false);
      }
    };

    fetchProfileData();

    if (userId) {
      fetchCars();
    }
  }, [userId, navigate, t, addToast]);

  // Calculate loadings
  const isLoading = profileLoading;

  if (isLoading) {
    return (
      <>
        <div className="loading-container">
          <Spinner size="large" customText={t('app.loading')} />
        </div>
      </>
    );
  }

  const TABS = [
    { id: 'ratings', label: t('profile.tab_ratings') },
    { id: 'achievements', label: t('profile.tab_achievements') },
    { id: 'vehicles', label: t('profile.tab_vehicles') },
  ];

  return (
    <>
      <div className="Myprofile-container">
        {/* User info section (always-visible header) */}
        <UserInfo
          profile={profile}
          onUpdateProfile={updateProfile}
          onUpdateAvatar={updateAvatar}
          validationErrors={validationErrors}
          isLoading={actionLoading}
        />

        {/* Tabbed sections */}
        <div className="profile-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={activeTab === tab.id}
              className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="profile-tab-panel" role="tabpanel">
          {activeTab === 'ratings' && <DetailedRatings userId={userId} />}
          {activeTab === 'achievements' && <UserBadges userId={userId} />}
          {activeTab === 'vehicles' && (
            <CarManager
              cars={cars}
              onAddCar={addCar}
              onDeleteCar={deleteCar}
              isLoading={actionLoading || carsLoading}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MyProfilePage;